import type { NonEmptyArray } from '../types/NonEmptyArray';

/**
 * Function parseInt has strange behavior as described in the documentation as "A string starting with an integer."
 * Any string that starts with an integer may thus be parsed into some number.
 * Example: parseInt('001289a019fb26a672faf56dabb15568d05efb32', 10) => 1289
 * Function below therefore parses the number string into integer when it is stable to do so.
 * Radix parameter is static since no other JavaScript function supports it,
 * so we can't easily check the validity of a given number string for other number systems.
 * @param {string} input
 * @returns {number|NaN}
 */
export function safeParseDecimalInt(input: string): number {
  if (Number.isFinite(+input)) {
    return Number.parseInt(input, 10);
  }
  return Number.NaN;
}

export function positiveModulo(dividend: number, divisor: number): number {
  return ((dividend % divisor) + divisor) % divisor;
}

export const roundTo = (fractionDigits: number): ((n: number) => number) => {
  const factor = 10 ** fractionDigits;
  return (n: number): number => Math.round(n * factor) / factor;
};

/**
 * Frequently used variant of rounding to two decimals
 */
export const roundTo2 = roundTo(2);

/**
 * pow ** operator may be noticeably slower than * and unnecessarily powerful tool
 * for just squaring a number.
 */
export function squared(a: number): number {
  return a * a;
}

export function isBetweenBounds(
  x: number,
  bound1: number,
  bound2: number,
  tolerance = 0,
): boolean {
  const [min, max] = bound1 > bound2 ? [bound2, bound1] : [bound1, bound2];
  return x >= min - tolerance && x <= max + tolerance;
}

export const FLOATING_POINT_ERROR = 1e-6;
export function isCloseTo(
  expected: number,
  actual: number,
  epsilon = FLOATING_POINT_ERROR,
): boolean {
  return Math.abs(expected - actual) < epsilon;
}

export const isNumber = (x: unknown): x is number => typeof x === 'number';

export const range = (start: number, end: number): number[] =>
  [...Array(end - start)].map((_, i) => i + start);

/**
 * Find element in array having maximum getValue(element)
 */
export const findByMax = <T>(
  array: NonEmptyArray<T>,
  getValue: (element: T) => number,
): T =>
  array.slice(1).reduce(
    (currentMax, element) => {
      const value = getValue(element);
      return value > currentMax.value ? { element, value } : currentMax;
    },
    { element: array[0], value: getValue(array[0]) },
  ).element;
